import {useCallback, useEffect, useMemo, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {useQuery} from 'react-query';
import {useLocation} from 'react-router';
import {useStoreContext} from '@store/store-context';
import {Body2} from '@ui/atoms/typography';
import {EmptyPlaceholder} from '@ui/atoms/empty-placeholder';
import {ItemLoader} from '@ui/organisms/item-loader';
import {cloudinary} from '@assets/images/cloudinary';
import {
  ICheckinCycle,
  ICheckinCycleDetail,
  ISpecificCheckinsWithResponse,
  ISpecificCheckinsWithResponseData,
} from '@pages/dashboard/company-legacy/company-components/company-checkins/company-checkins.interface';
import {
  ManagedCycle,
  ManagedSubmission,
  buildOccurrenceGroups,
} from '../team-management/team-management.utils';
import {CycleSelectorDropdown} from './cycle-selector-dropdown';
import {SendSelectorDropdown} from './components/send-selector-dropdown';
import {SubmissionRecord} from './components/submission-card';
import {TeamFeedResponseCard} from './components/team-feed-response-card';
import {getCreatorName} from './checkin-submit.utils';
import {
  getCheckinCycleId,
  useCheckinsWithCycles,
} from './hooks/useCheckinsWithCycles';

type HistorySubmission = ManagedSubmission & SubmissionRecord;

const getCheckinResponseData = (
  response?:
    | ISpecificCheckinsWithResponse
    | ISpecificCheckinsWithResponseData
    | false
    | null,
) => {
  if (!response) return null;
  return 'data' in response ? response.data : response;
};

const submissionTimestamp = (submission: SubmissionRecord) =>
  new Date(
    submission.submittedAt ||
      submission.createdAt ||
      0,
  ).valueOf();

const MyCheckinsHistory = observer(() => {
  const {
    authStore: {auth},
    usersStore: {users},
  } = useStoreContext();
  const location = useLocation();
  const [selectedCycleId, setSelectedCycleId] = useState<string | null>(null);
  const [selectedOccurrenceKey, setSelectedOccurrenceKey] = useState('');
  const [cycleSearchValue, setCycleSearchValue] = useState('');
  const notificationParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const requestedCycleId = notificationParams.get('cycleId');
  const requestedSend = notificationParams.get('send');
  const requestedComments = notificationParams.get('comments');

  const {
    enrichedSubmissions,
    checkinCycles,
    isLoading,
    checkinsController,
  } = useCheckinsWithCycles({
    userId: auth.user.id,
    isTeamFeed: false,
  });

  const cycles = useMemo(() => {
    const latestByCycle = new Map<string, number>();
    enrichedSubmissions.forEach((submission) => {
      const cycleId = getCheckinCycleId(submission);
      if (!cycleId) return;
      latestByCycle.set(
        cycleId,
        Math.max(
          latestByCycle.get(cycleId) || 0,
          submissionTimestamp(submission),
        ),
      );
    });

    return [...checkinCycles].sort(
      (left, right) =>
        (latestByCycle.get(right.id) || 0) -
        (latestByCycle.get(left.id) || 0),
    );
  }, [checkinCycles, enrichedSubmissions]);

  useEffect(() => {
    if (!cycles.length) {
      setSelectedCycleId(null);
      return;
    }
    const requestedCycle = requestedCycleId
      ? cycles.find((cycle) => cycle.id === requestedCycleId)
      : undefined;
    if (requestedCycle && requestedCycle.id !== selectedCycleId) {
      setSelectedCycleId(requestedCycle.id);
      setSelectedOccurrenceKey('');
      return;
    }
    if (!cycles.some((cycle) => cycle.id === selectedCycleId)) {
      setSelectedCycleId(cycles[0].id);
      setSelectedOccurrenceKey('');
    }
  }, [cycles, requestedCycleId, selectedCycleId]);

  const selectedCycle = useMemo(
    () => cycles.find((cycle) => cycle.id === selectedCycleId) || null,
    [cycles, selectedCycleId],
  );
  const cycleSubmissions = useMemo(
    () =>
      enrichedSubmissions
        .filter(
          (submission) => getCheckinCycleId(submission) === selectedCycleId,
        )
        .sort(
          (left, right) =>
            submissionTimestamp(right) - submissionTimestamp(left),
        ) as HistorySubmission[],
    [enrichedSubmissions, selectedCycleId],
  );
  const occurrences = useMemo(
    () =>
      selectedCycle
        ? buildOccurrenceGroups(
            cycleSubmissions,
            selectedCycle as ManagedCycle,
            auth.user.workspace?.timezone,
          )
        : [],
    [
      auth.user.workspace?.timezone,
      cycleSubmissions,
      selectedCycle,
    ],
  );

  useEffect(() => {
    if (!occurrences.length) {
      setSelectedOccurrenceKey('');
      return;
    }
    const requestedOccurrence = requestedSend
      ? occurrences.find(({key}) => key === requestedSend)
      : undefined;
    if (requestedOccurrence && requestedOccurrence.key !== selectedOccurrenceKey) {
      setSelectedOccurrenceKey(requestedOccurrence.key);
      return;
    }
    if (!occurrences.some(({key}) => key === selectedOccurrenceKey)) {
      setSelectedOccurrenceKey(occurrences[0].key);
    }
  }, [occurrences, requestedSend, selectedOccurrenceKey]);

  const selectedOccurrence =
    occurrences.find(({key}) => key === selectedOccurrenceKey) ||
    occurrences[0];
  const selectedSubmission = useMemo(() => {
    const rows = (selectedOccurrence?.submissions || []) as HistorySubmission[];
    return [...rows].sort(
      (left, right) =>
        submissionTimestamp(right) - submissionTimestamp(left),
    )[0];
  }, [selectedOccurrence]);
  const displayedSubmission = useMemo<HistorySubmission | undefined>(() => {
    if (!selectedSubmission) return undefined;
    return {
      ...selectedSubmission,
      user:
        typeof selectedSubmission.user === 'object'
          ? selectedSubmission.user
          : auth.user,
    } as HistorySubmission;
  }, [auth.user, selectedSubmission]);
  const getCycleSenderLabel = useCallback(
    (cycle: ICheckinCycle) => {
      const creatorName = getCreatorName(cycle.createdBy, [
        auth.user,
        ...users,
      ]);
      return creatorName === 'Cycle creator'
        ? 'Sent by cycle creator'
        : `Sent by ${creatorName}`;
    },
    [auth.user, users],
  );

  const {
    data: responseDetail,
    isLoading: isLoadingDetail,
    isError: detailError,
    refetch: refetchDetail,
  } = useQuery(
    ['my-checkin-history-response', auth.user.id, selectedSubmission?.id],
    async () => {
      const response = await checkinsController.getCheckinByIdAndFetchResponses(
        selectedSubmission?.id || '',
      );
      const detail = getCheckinResponseData(response);
      if (!detail) throw new Error('Unable to load response');
      return detail;
    },
    {
      enabled: Boolean(selectedSubmission?.id),
      refetchOnWindowFocus: false,
      retry: false,
    },
  );

  if (isLoading && !selectedCycle) {
    return (
      <div className="flex justify-center py-8">
        <ItemLoader />
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-6">
      <section className="flex flex-col gap-4 rounded-[10px] border border-borderLight bg-white p-4 sm:p-6">
        <div className="flex flex-col gap-2">
          <Body2 kind="textDark" weight="semibold">
            Cycle
          </Body2>
          <CycleSelectorDropdown
            cycles={cycles}
            selectedCycleName={
              selectedCycle?.cycleName || 'No cycles available'
            }
            searchValue={cycleSearchValue}
            onSearchChange={setCycleSearchValue}
            onCycleSelect={(cycleId) => {
              setSelectedCycleId(cycleId);
              setSelectedOccurrenceKey('');
            }}
            getCycleSecondaryLabel={getCycleSenderLabel}
            showStatusDots={false}
            fullWidth
          />
        </div>
        <div className="flex flex-col gap-2">
          <Body2 kind="textDark" weight="semibold">
            Send
          </Body2>
          <SendSelectorDropdown
            occurrences={occurrences}
            selectedOccurrence={selectedOccurrence}
            onSelect={setSelectedOccurrenceKey}
          />
        </div>
      </section>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <ItemLoader />
        </div>
      ) : displayedSubmission ? (
        <section aria-label="Personal check-in response">
          <TeamFeedResponseCard
            submission={displayedSubmission}
            mentionableUserIds={[]}
            detail={responseDetail}
            cycle={selectedCycle as ICheckinCycleDetail | null}
            isLoadingDetail={isLoadingDetail}
            detailError={detailError}
            onRetryDetail={() => refetchDetail()}
            defaultCommentsOpen={selectedSubmission?.id === requestedComments}
          />
        </section>
      ) : (
        <section className="rounded-[10px] border border-borderLight bg-white">
          <EmptyPlaceholder
            title="No check-in history yet"
            subtitle="Your submitted check-ins will appear here."
            image={{
              alt: 'No check-in history yet',
              shadow: '-10px 44px 28px 0 rgba(19, 17, 17, 0.12)',
              src: cloudinary.emptyGoals,
              style: {height: '140px', width: '130px'},
            }}
          />
        </section>
      )}
    </div>
  );
});

export const History = MyCheckinsHistory;
export default History;
